import { useState } from 'react';
import { Check, HeartPulse, Info } from 'lucide-react';

export type SaqDomainKey =
  | 'physicalLimitation'
  | 'anginaStability'
  | 'anginaFrequency'
  | 'treatmentSatisfaction'
  | 'qualityOfLife';

export type SaqDomainScores = Record<SaqDomainKey, number | null>;

export interface SAQResultPayload {
  physicalAnswers: number[];
  stability: number;
  frequency: { episodes: number; nitro: number };
  satisfactionAnswers: number[];
  qolAnswers: number[];
  domainScores: SaqDomainScores;
  interpretation: string[];
  finishedAt?: string;
}

interface SAQTestProps {
  initial?: SAQResultPayload;
  onCancel?: () => void;
  onNext: (result: SAQResultPayload) => void;
}

export const SAQ_DOMAIN_LABELS: Record<SaqDomainKey, string> = {
  physicalLimitation: 'Limitação física',
  anginaStability: 'Estabilidade da angina',
  anginaFrequency: 'Frequência da angina',
  treatmentSatisfaction: 'Satisfação com o tratamento',
  qualityOfLife: 'Qualidade de vida',
};

export const SAQ_DOMAIN_SEQUENCE: SaqDomainKey[] = [
  'physicalLimitation',
  'anginaStability',
  'anginaFrequency',
  'treatmentSatisfaction',
  'qualityOfLife',
];

export const SAQ_PHYSICAL_ITEMS = [
  'Caminhar dentro de casa ou em terreno plano',
  'Subir um lance de escada sem parar',
  'Carregar compras ou sacolas leves',
  'Realizar tarefas domésticas (varrer, arrumar a cama etc.)',
  'Tomar banho e vestir-se sem ajuda',
  'Atividade sexual',
  'Trabalhar ou realizar hobbies fora de casa',
] as const;

export const SAQ_LIMITATION_OPTIONS = [
  { label: 'Sem limitação', helper: 'Consegue normalmente', score: 100 },
  { label: 'Limitação pequena', helper: 'Sente leve desconforto', score: 75 },
  { label: 'Limitação moderada', helper: 'Precisa mudar o ritmo', score: 50 },
  { label: 'Limitação importante', helper: 'Consegue apenas parcialmente', score: 25 },
  { label: 'Não consegue realizar', helper: 'Evita a atividade', score: 0 },
] as const;

export const SAQ_STABILITY_OPTIONS = [
  { label: 'Muito pior que há 4 semanas', score: 0 },
  { label: 'Um pouco pior', score: 25 },
  { label: 'Sem mudanças', score: 50 },
  { label: 'Um pouco melhor', score: 75 },
  { label: 'Muito melhor', score: 100 },
] as const;

export const SAQ_FREQUENCY_OPTIONS = [
  { label: 'Nenhum episódio nas últimas 4 semanas', score: 100 },
  { label: 'Menos de 1 vez por semana', score: 80 },
  { label: '1–2 vezes por semana', score: 60 },
  { label: '3–4 vezes por semana', score: 40 },
  { label: 'Quase todos os dias', score: 20 },
  { label: 'Várias vezes ao dia', score: 0 },
] as const;

export const SAQ_SATISFACTION_ITEMS = [
  'Quão satisfeito está com o tratamento cardíaco atual?',
  'O tratamento atual controla seus sintomas do peito?',
  'Você se sente confiante em continuar com esse tratamento?',
] as const;

export const SAQ_LIKERT_OPTIONS = [
  { label: 'Muito insatisfeito', score: 0 },
  { label: 'Insatisfeito', score: 25 },
  { label: 'Neutro', score: 50 },
  { label: 'Satisfeito', score: 75 },
  { label: 'Muito satisfeito', score: 100 },
] as const;

export const SAQ_QOL_ITEMS = [
  'Quanto a angina interfere no prazer de viver?',
  'Se permanecesse assim por toda a vida, você se sentiria bem?',
  'Quanto a condição cardíaca limita a vida social e familiar?',
] as const;

export const SAQ_QOL_OPTIONS = [
  { label: 'Impacto extremo / vida muito ruim', score: 0 },
  { label: 'Impacto grande', score: 25 },
  { label: 'Impacto moderado', score: 50 },
  { label: 'Impacto leve', score: 75 },
  { label: 'Quase nenhum impacto', score: 100 },
] as const;

const averageScore = (answers: number[], options: readonly { score: number }[]) => {
  const valid = answers.filter((index) => index >= 0).map((index) => options[index]?.score ?? 0);
  if (!valid.length) return null;
  return Number((valid.reduce((sum, value) => sum + value, 0) / valid.length).toFixed(1));
};

export const describeSaqScore = (score: number | null) => {
  if (score == null) return 'Sem dados';
  return `Valor adaptado legado: ${score.toFixed(1)}/100`;
};

export default function SAQTest({ initial, onCancel, onNext }: SAQTestProps) {
  const [physical, setPhysical] = useState<number[]>(initial?.physicalAnswers ?? Array(SAQ_PHYSICAL_ITEMS.length).fill(-1));
  const [stability, setStability] = useState<number>(initial?.stability ?? -1);
  const [episodes, setEpisodes] = useState<number>(initial?.frequency?.episodes ?? -1);
  const [nitro, setNitro] = useState<number>(initial?.frequency?.nitro ?? -1);
  const [satisfaction, setSatisfaction] = useState<number[]>(initial?.satisfactionAnswers ?? Array(SAQ_SATISFACTION_ITEMS.length).fill(-1));
  const [qol, setQol] = useState<number[]>(initial?.qolAnswers ?? Array(SAQ_QOL_ITEMS.length).fill(-1));

  const setArrayAnswer = (setter: React.Dispatch<React.SetStateAction<number[]>>) => (index: number, value: number) => {
    setter((previous) => {
      const next = [...previous];
      next[index] = value;
      return next;
    });
  };

  const domainScores: SaqDomainScores = {
    physicalLimitation: averageScore(physical, SAQ_LIMITATION_OPTIONS),
    anginaStability: stability >= 0 ? SAQ_STABILITY_OPTIONS[stability].score : null,
    anginaFrequency:
      episodes >= 0 && nitro >= 0
        ? Number(((SAQ_FREQUENCY_OPTIONS[episodes].score + SAQ_FREQUENCY_OPTIONS[nitro].score) / 2).toFixed(1))
        : null,
    treatmentSatisfaction: averageScore(satisfaction, SAQ_LIKERT_OPTIONS),
    qualityOfLife: averageScore(qol, SAQ_QOL_OPTIONS),
  };

  const interpretation = (Object.entries(domainScores) as [SaqDomainKey, number | null][])
    .filter(([, value]) => value != null)
    .map(([key, value]) => `${SAQ_DOMAIN_LABELS[key]}: ${describeSaqScore(value)}`);

  const allAnswered =
    physical.every((value) => value >= 0) &&
    stability >= 0 &&
    episodes >= 0 &&
    nitro >= 0 &&
    satisfaction.every((value) => value >= 0) &&
    qol.every((value) => value >= 0);

  const setPhysicalAnswer = setArrayAnswer(setPhysical);
  const setSatisfactionAnswer = setArrayAnswer(setSatisfaction);
  const setQolAnswer = setArrayAnswer(setQol);

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">Seattle Angina Questionnaire (SAQ)</p>
          <p className="text-sm opacity-90">
            Considere apenas as últimas 4 semanas. Não existem respostas certas ou erradas; precisamos entender como a angina impacta seu dia a dia, tratamento e qualidade de vida.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
        {(Object.entries(domainScores) as [SaqDomainKey, number | null][]).map(([key, value]) => (
          <div key={key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{SAQ_DOMAIN_LABELS[key]}</div>
            <div className="mt-3 text-3xl font-black text-slate-900">{value != null ? value.toFixed(1) : '—'}</div>
            <div className="mt-2 text-sm text-slate-600">{describeSaqScore(value)}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
          <HeartPulse className="h-4 w-4" />
          Limitação física (7 itens)
        </div>
        <p className="mb-3 text-xs text-slate-500">Para cada atividade, marque o quanto a angina limitou você nas últimas 4 semanas.</p>
        <div className="space-y-3">
          {SAQ_PHYSICAL_ITEMS.map((item, index) => (
            <div key={item} className="rounded-xl border border-slate-200 p-3">
              <div className="font-medium text-slate-800">{item}</div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-5 text-xs">
                {SAQ_LIMITATION_OPTIONS.map((option, optionIndex) => (
                  <label
                    key={option.label}
                    className={`flex cursor-pointer flex-col gap-1 rounded-lg border p-2 ${
                      physical[index] === optionIndex ? 'border-blue-600 bg-blue-600 text-white' : 'bg-white hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      checked={physical[index] === optionIndex}
                      onChange={() => setPhysicalAnswer(index, optionIndex)}
                    />
                    <span className="text-sm font-semibold">{option.label}</span>
                    <span className="text-[11px] opacity-80">{option.helper}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-700">Estabilidade da angina</div>
          <p className="mt-1 text-xs text-slate-500">Compare sua condição hoje com 4 semanas atrás.</p>
          <div className="mt-3 space-y-2">
            {SAQ_STABILITY_OPTIONS.map((option, index) => (
              <label
                key={option.label}
                className={`block cursor-pointer rounded-lg border p-2 text-sm ${
                  stability === index ? 'border-blue-600 bg-blue-600 text-white' : 'hover:bg-slate-50'
                }`}
              >
                <input type="radio" className="sr-only" checked={stability === index} onChange={() => setStability(index)} />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-700">Frequência da angina</div>
          <p className="mt-1 text-xs text-slate-500">Frequência dos sintomas e do uso de medicação de alívio nas últimas 4 semanas.</p>
          <div className="mt-3 space-y-3">
            <div>
              <div className="text-sm font-medium">Dor/pressão no peito</div>
              <div className="mt-1 grid gap-2 sm:grid-cols-2">
                {SAQ_FREQUENCY_OPTIONS.map((option, index) => (
                  <label
                    key={`episodes-${option.label}`}
                    className={`cursor-pointer rounded-lg border p-2 text-xs ${
                      episodes === index ? 'border-blue-600 bg-blue-600 text-white' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input type="radio" className="sr-only" checked={episodes === index} onChange={() => setEpisodes(index)} />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium">Uso de nitrato / medicação de resgate</div>
              <div className="mt-1 grid gap-2 sm:grid-cols-2">
                {SAQ_FREQUENCY_OPTIONS.map((option, index) => (
                  <label
                    key={`nitro-${option.label}`}
                    className={`cursor-pointer rounded-lg border p-2 text-xs ${
                      nitro === index ? 'border-blue-600 bg-blue-600 text-white' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input type="radio" className="sr-only" checked={nitro === index} onChange={() => setNitro(index)} />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-700">Satisfação com o tratamento</div>
          <div className="mt-3 space-y-2">
            {SAQ_SATISFACTION_ITEMS.map((item, index) => (
              <div key={item} className="rounded-xl border border-slate-200 p-3">
                <div className="font-medium text-slate-800">{item}</div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {SAQ_LIKERT_OPTIONS.map((option, optionIndex) => (
                    <label
                      key={`${item}-${option.label}`}
                      className={`cursor-pointer rounded-full border px-3 py-2 ${
                        satisfaction[index] === optionIndex ? 'border-green-600 bg-green-600 text-white' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        checked={satisfaction[index] === optionIndex}
                        onChange={() => setSatisfactionAnswer(index, optionIndex)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-700">Qualidade de vida</div>
          <div className="mt-3 space-y-2">
            {SAQ_QOL_ITEMS.map((item, index) => (
              <div key={item} className="rounded-xl border border-slate-200 p-3">
                <div className="font-medium text-slate-800">{item}</div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {SAQ_QOL_OPTIONS.map((option, optionIndex) => (
                    <label
                      key={`${item}-${option.label}`}
                      className={`cursor-pointer rounded-full border px-3 py-2 ${
                        qol[index] === optionIndex ? 'border-purple-600 bg-purple-600 text-white' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        checked={qol[index] === optionIndex}
                        onChange={() => setQolAnswer(index, optionIndex)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">
            Todos os itens respondidos?{' '}
            <span className={`font-semibold ${allAnswered ? 'text-green-600' : 'text-red-500'}`}>
              {allAnswered ? 'Sim' : 'Não'}
            </span>
          </div>
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-white disabled:opacity-50"
            onClick={() =>
              allAnswered &&
              onNext({
                physicalAnswers: physical,
                stability,
                frequency: { episodes, nitro },
                satisfactionAnswers: satisfaction,
                qolAnswers: qol,
                domainScores,
                interpretation,
                finishedAt: new Date().toISOString(),
              })
            }
            disabled={!allAnswered}
          >
            Concluir SAQ
            <Check className="ml-1 h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
